import React from 'react'
import { cn } from '@/lib/utils'
import { getInitials } from '@/utils/formatting'

interface AvatarProps {
  name?: string
  src?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  status?: 'online' | 'offline' | 'away' | 'busy'
}

const sizeClasses = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
}

const statusColors = {
  online:  'bg-moss-400',
  offline: 'bg-ink-500',
  away:    'bg-honey-400',
  busy:    'bg-oxblood-400',
}

const statusSizes = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
  xl: 'w-3.5 h-3.5',
}

export function Avatar({ name, src, size = 'md', className, status }: AvatarProps) {
  return (
    <div className={cn('relative inline-flex shrink-0', className)}>
      {src ? (
        <img
          src={src}
          alt={name}
          className={cn('rounded-full object-cover border hairline', sizeClasses[size])}
        />
      ) : (
        <div
          aria-hidden="true"
          className={cn(
            'rounded-full flex items-center justify-center font-semibold',
            sizeClasses[size]
          )}
          style={{
            background: 'linear-gradient(135deg, rgba(243,223,147,0.20) 0%, rgba(255,197,160,0.20) 100%)',
            border: '0.5px solid rgba(255,255,255,0.10)',
            color: 'rgba(255,255,255,0.80)',
          }}
        >
          {name ? getInitials(name) : '?'}
        </div>
      )}
      {status && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-2 border-obsidian-900',
            statusColors[status],
            statusSizes[size]
          )}
        />
      )}
    </div>
  )
}
